import { Component, OnInit, OnDestroy, HostListener, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, distinctUntilChanged, interval, map, Subscription, takeWhile } from 'rxjs';

import { environment } from '@environment';

import { UsernameType, PATH_DEFINITIONS, RAPPIDER_THEME_SETTINGS, AppRightSidebarTab } from '@rappider/shared/definitions';
import { BrowserStorageManagementService, LanguageService, RoutingService, ScrollService } from '@rappider/services';

import { UserInterface, ProjectInterface, PageInterface } from '@rappider/api-sdk';
import { Logout, SendVerifyEmail, SendVerifySms } from 'libs/authentication/src/lib/state/authentication.actions';
import { selectClientLocalStates } from 'libs/shared/src/lib/states/client-local-state/client-local-state.selectors';

import {
  PERSON_EMAIL_VERIFY_ALERT_CONFIG, PERSON_PHONE_NUMBER_VERIFY_ALERT_CONFIG,
  PROJECT_HOME_FEATURES_CONFIG,
  PROJECT_MENU_CONFIG,
  WORKSPACE_FOOTER_MENU,
  WORKSPACE_LOGOUT_MENU,
  WORKSPACE_MENU
} from '@rappider/shared/configs';

import { AlertConfig, Menu, MenuActionBehavior, MenuMode } from '@rappider/rappider-components/utils';
import { ThemeMode } from '@rappider/models';
import { Person, ProcessInfoWithRelations, ProjectDesignTask } from '@rappider/rappider-sdk';
import { Navigate, sidebarToggleAnimation } from '@rappider/shared';
import { ChangeAppSearchVisibility, CollapseLeftSidebar, ExpandLeftSidebar, ToggleRightSidebarVisibility } from './state/app.actions';
import { homePricingConfigApp } from './utils/home-pricing-app.config';
import { SubscriptionAlertVisibility } from 'libs/payment/src/lib/state/payment.actions';
import { Permission } from './components/header/utils/admin-dashboard-permissions.enum';
import { TierType } from 'libs/authentication/src/lib/utils/tier-type.enum';
import { WizardStepConfig, WizardStepStatus } from 'libs/project-design/src/lib/configs/wizard-steps';
import { cloneDeep } from 'lodash';
import { ProjectDesignService } from 'libs/project-design/src/lib/services/project-design.service';
import { selectProjectDesignTasks } from 'libs/project-design/src/lib/state/project-design-task/project-design-task.selectors';
import { NextStep, TriggerProjectOrchestration } from 'libs/project-design/src/lib/state/project-design-task/project-design-task.actions';
import { LayoutMode } from 'libs/project-design/src/lib/configs/layoutMode.enum';
import { PermissionService } from '@rappider/services';
import { JoyrideService } from 'ngx-joyride';

@Component({
  selector: 'rappider-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    sidebarToggleAnimation
  ]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {

  /* authentication variables */
  authenticatedUser: UserInterface;
  activePerson: Person;
  activePersonOnboardingCompleted: boolean | undefined;
  /* user permissions */
  canInviteUsers = false;

  PERSON_VERIFICATION_ALERT_CONFIG: AlertConfig;

  isMenuVisible = true;
  isPersonVerified = false;
  isAlertVisible = true;
  isFooterVisible = true;
  isHeaderVisible = true;

  rightSidebarVisibility = false;
  isLayoutsVisible = true;
  isHomePricingVisible = false;
  isHomePricingVisiblePermanently = false;
  isRelativeSplitter = false;

  activeRightSidebarTabName = null;

  noInnerContentPadding = true;
  displayLivePreviewNotification = false;
  pagesMenu = null;
  workSpaceMenu = WORKSPACE_MENU;

  // workSpaceMenuDropdown = WORKSPACE_MENU_DROPDOWN;
  workSpaceFooterMenu = WORKSPACE_FOOTER_MENU;
  workspaceLogoutMenu = WORKSPACE_LOGOUT_MENU;
  projectMegaMenuFeatures = PROJECT_HOME_FEATURES_CONFIG;
  rappiderThemeSettings = RAPPIDER_THEME_SETTINGS;
  title = 'rappider';
  subscriptions: Subscription[] = [];
  activeProject: ProjectInterface;
  pages: PageInterface[];
  isWorkSpaceMenuCollapsed: boolean | undefined;
  isWorkSpaceMenuPinned: boolean | undefined;
  projectListPath = PATH_DEFINITIONS.PROJECTS.PROJECT_LIST_PATH;
  selectedTheme: ThemeMode = ThemeMode.Dark;
  // page title saved on the route data
  routePageTitle: string;
  /* flag to enable the page title on the header */
  showPageTitle = false;

  projectFeatures = PROJECT_MENU_CONFIG;

  megaMenuVisible = false;

  deployAlertConfig = {
    title: '',
    description: '',
    titleIcon: ''
  };

  /* Used to scale app, small:1 , medium: 1.25, large 1,5 */
  defaultScale = 1;

  /**
   * The deployment status indicates whether the initiation of the deployment process
   * was successful or if an error occurred during its commencement;
   * it does not imply the completion or non-completion of the deployment itself.
   * @type {DeployStatus}
   * @memberof AppComponent
   */
  // deployStatus: DeployStatus;
  // DeployStatus = DeployStatus;

  /* active route path */
  activePath: string;

  appSearchVisibility;

  homePricingConfigApp = homePricingConfigApp;

  isCustomizationModalVisible = true;

  splitterLeftContainerSizeDefault = 40; // percentage
  splitterRightContainerSizeDefault = 60; // percentage
  splitterSourceCodeContainerSizeDefault = 50; // percentage
  splitterRouterOutletContainerSizeDefault = 50; // percentage

  splitterLeftContainerSize = this.splitterLeftContainerSizeDefault; // percentage
  splitterRightContainerSize = this.splitterRightContainerSizeDefault; // percentage
  splitterSourceCodeContainerSize = this.splitterSourceCodeContainerSizeDefault; // percentage
  splitterRouterOutletContainerSize = this.splitterRouterOutletContainerSizeDefault; // percentage

  // Display the layout root component on the left side of the screen
  layoutMode: LayoutMode | undefined | null;
  LayoutMode = LayoutMode;

  clientLocalState: Record<string, any> = {};

  // wizard state
  WizardStepStatus = WizardStepStatus;
  wizardStepsConfigs: WizardStepConfig[] | undefined;
  activeWizardStep = 0;

  displayWizardIntroduction$ = new BehaviorSubject<boolean>(false);

  // Displays the rabbit wizard modal
  displayWizardModal = false;
  // Displays the wizard in the project design page
  displayWizard = false;

  projectDesignTasksLoaded = false;
  projectDesignTasks: ProjectDesignTask[] = [];
  projectCreateKeyIsCompleted = false;

  // To track if there is any open overlaid submenu (for horizontal menu mode)
  // If there is an open overlaid submenu, the left sidebar should not be auto-collapsed
  hasOpenOverlaidSubmenu = false;
  workspaceMenuIsHovering = false;

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.appSearchVisibility) {
      this.store.dispatch(new ChangeAppSearchVisibility());
    }
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'f') {
      this.store.dispatch(new ChangeAppSearchVisibility());
      return false;
    }
  }

  constructor(
    // TODO: Fix type issue below
    private store: Store<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    private router: Router,
    private routingService: RoutingService,
    private scrollService: ScrollService,
    private languageService: LanguageService,
    private activatedRoute: ActivatedRoute,
    private browserStorageManagementService: BrowserStorageManagementService,
    private projectDesignService: ProjectDesignService,
    private permissionService: PermissionService,
    private readonly joyrideService: JoyrideService
  ) { }

  ngOnInit(): void {
    console.log('*** AppComponent ngOnInit');
    this.subscribeToData();
    this.setThemeBySystemTheme();
    this.verifyPerson();
    this.getSplitterSizesToFromStorage();
    this.initWatcher();
  }

  /**
   *
   *
   * @memberof AppComponent
   */
  ngOnDestroy(): void {
    console.log('*** AppComponent ngOnDestroy');
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  ngAfterViewInit(): void {
    console.log('*** AppComponent ngAfterViewInit');
    this.setScale(this.defaultScale);
    this.triggerToCollapseWorkspaceMenu();
  }

  /**
   * Init application theme based on the system preference
   * Flow as below
   * 1 - If there is a saved mode in the person data - use this as the initial value
   * 2 - If there is no saved mode, Check for the preferred device theme
   * 3 - If device theme is dark, set the init value to `dark`
   * 4 - Else set the default value to `light`
   */
  setThemeBySystemTheme() {
    console.log('*** AppComponent setThemeBySystemTheme');
    // remove old class
    document.body.classList.remove(this.selectedTheme);
    if (this.activePerson?.preferredTheme || this.browserStorageManagementService.getLocalStorageItem('preferredTheme')) {
      if (this.activePerson?.preferredTheme) {
        this.selectedTheme = this.activePerson.preferredTheme as ThemeMode;
      } else {
        this.selectedTheme = this.browserStorageManagementService.getLocalStorageItem('preferredTheme') as ThemeMode;
      }
      // add new class
      document.body.classList.add(this.selectedTheme);
      this.browserStorageManagementService.setLocalStorageItem('preferredTheme', this.selectedTheme);
    } else {
      // const deviceMode = window.matchMedia('(prefers-color-scheme: dark)');
      // this.selectedTheme = deviceMode.matches ? ThemeMode.Dark : ThemeMode.Light;
      this.selectedTheme = ThemeMode.Dark;
      // add new class
      document.body.classList.add(this.selectedTheme);
      this.browserStorageManagementService.setLocalStorageItem('preferredTheme', this.selectedTheme);

    }
  }

  /**
   * Subscribe all data
   *
   * @memberof AppComponent
   */
  subscribeToData() {
    console.log('*** AppComponent subscribeToData');
    this.subscriptions = [
      this.subscribeToNavigation(),
      this.subscribeToActivePerson(),
      this.subscribeToUser(),
      this.subscribeToActiveProject(),
      this.subscribeToPermissions(),
      this.subscribeToAppSearchVisibility(),
      this.subscribeToLeftSideBarCollapseStatus(),
      this.subscribeToLeftSideBarMenuPinned(),
      this.subscribeToRightSidebarTabName(),
      this.subscribeToRightSidebarVisibility(),
      this.subscribeToProcessInfos()
    ];
  }

  subscribeToDataAfterActiveProject() {
    console.log('*** AppComponent subscribeToDataAfterActiveProject');
    this.subscriptions.push(
      this.subscribeToClientLocalState(),
      this.subscribeToProjectDesignTasks())
      ;
  }


  updateWorkspaceMenuHomePageItemPath() {
    console.log('*** AppComponent updateWorkspaceMenuHomePageItemPath');
    const projectHomePath = this.activeProject?.id
      ? `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${this.activeProject.id}`
      : PATH_DEFINITIONS.PROJECTS.PROJECT_ROOT;

    const updateMenuItemPath = (menuItem) => {
      if (menuItem) {
        menuItem.redirectUrl = projectHomePath;
      }
    };

    updateMenuItemPath(this.workSpaceMenu.items.find(m => m.key === 'project-home'));
    // updateMenuItemPath(this.workSpaceMenuDropdown.items.find(m => m.key === 'project-home'));
    const isChannelsMenuExpanded = this.workSpaceMenu.items.find(item => item.key === 'channels');
    if (!this.isWorkSpaceMenuCollapsed) {
      this.workSpaceMenu.mode = MenuMode.Inline;
      isChannelsMenuExpanded.isExpanded = true;
    } else {
      this.workSpaceMenu.mode = MenuMode.Vertical;
      isChannelsMenuExpanded.isExpanded = false;
    }
    this.workSpaceMenu = { ...this.workSpaceMenu };
    // this.workSpaceMenuDropdown = { ...this.workSpaceMenuDropdown };
  }

  /**
   * subscribes to router event to get information about path
   *
   * @returns
   * @memberof AppComponent
   */
  subscribeToNavigation() {
    console.log('*** AppComponent subscribeToNavigation');
    return this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isAlertVisible = !(this.routingService.getRouteDataByFieldName('alertVisibility') === false);
        this.isMenuVisible = !(this.routingService.getRouteDataByFieldName('isSideMenuVisible') === false);
        this.isFooterVisible = !(this.routingService.getRouteDataByFieldName('isFooterVisible') === false);
        this.isHeaderVisible = !(this.routingService.getRouteDataByFieldName('isHeaderVisible') === false);
        this.noInnerContentPadding = !!this.routingService.getRouteDataByFieldName('noInnerContentPadding');
        this.displayLivePreviewNotification = !(this.routingService.getRouteDataByFieldName('displayLivePreviewNotification') === false);
        this.isLayoutsVisible = !(this.routingService.getRouteDataByFieldName('isLayoutsVisible') === false);
        this.isRelativeSplitter = !(this.routingService.getRouteDataByFieldName('isRelativeSplitter') === false);
        // this.isHomePricingVisible = this.isHomePricingVisiblePermanently ? (this.routingService.getRouteDataByFieldName('isHomePricingVisible') === false) : false;
        this.activePath = event.url;

        if (this.routingService?.getRouteDataByFieldName('layoutMode')) {
          const routeLayoutMode = this.routingService.getRouteDataByFieldName('layoutMode') as LayoutMode;
          this.setLayoutMode(routeLayoutMode);
        } else {
          this.setSplitterLayout(null);
        }

        let currentRoute = this.activatedRoute.snapshot;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }

        // Now, currentRoute is the deepest active child route.
        const data = currentRoute.data;
        this.routePageTitle = data?.title || null;
        this.showPageTitle = data?.showPageTitle;
      }
    });
  }


  setSplitterLayout(layout: LayoutMode | undefined | null) {
    console.log('*** AppComponent setSplitterLayout', layout);
    let newLeftSize: number;
    let newRightSize: number;
    let newSourceCodeSize: number;
    let newRouterOutletSize: number;

    switch (layout) {
      case LayoutMode.Hidden:
        newLeftSize = 0;
        newRightSize = 100;
        newSourceCodeSize = 0;
        newRouterOutletSize = 100;
        break;
      case LayoutMode.ProjectDesign:
        newLeftSize = parseInt(localStorage.getItem('splitterLeftContainerSize'), 10) ?? this.splitterLeftContainerSizeDefault;
        newRightSize = parseInt(localStorage.getItem('splitterRightContainerSize'), 10) ?? this.splitterRightContainerSizeDefault;
        newSourceCodeSize = 0;
        newRouterOutletSize = 100;
        break;
      case LayoutMode.SourceCode:
        newLeftSize = this.splitterLeftContainerSizeDefault;
        newRightSize = this.splitterRightContainerSizeDefault;
        newSourceCodeSize = 100;
        newRouterOutletSize = 0;
        break;
      case LayoutMode.TPM:
        newLeftSize = this.splitterLeftContainerSizeDefault;
        newRightSize = this.splitterRightContainerSizeDefault;
        newSourceCodeSize = this.splitterSourceCodeContainerSizeDefault;
        newRouterOutletSize = this.splitterRouterOutletContainerSizeDefault;
        break;
      default:
        newLeftSize = parseInt(localStorage.getItem('splitterLeftContainerSize'), 10) ?? 0;
        newRightSize = parseInt(localStorage.getItem('splitterRightContainerSize'), 10) ?? 100;
        newSourceCodeSize = 0;
        newRouterOutletSize = 100;
        break;
    }

    if (this.splitterLeftContainerSize !== newLeftSize ||
      this.splitterRightContainerSize !== newRightSize ||
      this.splitterSourceCodeContainerSize !== newSourceCodeSize ||
      this.splitterRouterOutletContainerSize !== newRouterOutletSize) {
      this.splitterLeftContainerSize = newLeftSize;
      this.splitterRightContainerSize = newRightSize;
      this.splitterSourceCodeContainerSize = newSourceCodeSize;
      this.splitterRouterOutletContainerSize = newRouterOutletSize;
    }

    this.saveSplitterSizesToLocalStorage(); // save the sizes to local storage to load them on next initialization
  }

  /**
   * Save the splitter sizes to local storage
   */
  saveSplitterSizesToLocalStorage() {
    localStorage.setItem('splitterLeftContainerSize', String(this.splitterLeftContainerSize));
    localStorage.setItem('splitterRightContainerSize', String(this.splitterRightContainerSize));
  }

  getSplitterSizesToFromStorage() {
    const splitterLeftContainerSize = localStorage.getItem('splitterLeftContainerSize');
    const splitterRightContainerSize = localStorage.getItem('splitterRightContainerSize');
    if (splitterLeftContainerSize !== null && splitterRightContainerSize !== null) {
      this.splitterLeftContainerSize = parseInt(splitterLeftContainerSize, 10);
      this.splitterRightContainerSize = parseInt(splitterRightContainerSize, 10);
    } else {
      this.splitterLeftContainerSize = this.splitterLeftContainerSizeDefault;
      this.splitterRightContainerSize = this.splitterRightContainerSizeDefault;
    }
  }

  subscribeToRightSidebarTabName() {
    console.log('*** AppComponent   subscribeToRightSidebarTabName');
    return this.store.select(state => state.app.rightSidebarTabName).subscribe(rightSidebarTabName => {
      this.activeRightSidebarTabName = rightSidebarTabName;
    });
  }

  subscribeToRightSidebarVisibility() {
    console.log('*** AppComponent subscribeToRightSidebarVisibility');
    return this.store.select(state => state.app.rightSidebarVisibility).subscribe(rightSidebarVisibility => {
      this.rightSidebarVisibility = rightSidebarVisibility;
    });
  }

  subscribeToProcessInfos() {
    return this.store.select(state => state.processInfo.data).subscribe((processInfos: ProcessInfoWithRelations[]) => {
      if (processInfos?.length && !this.projectCreateKeyIsCompleted) {
        const projectCreateKey = processInfos.find(processInfo => processInfo.identifier === 'project-create');
        if (projectCreateKey?.status === 'completed' && projectCreateKey.conclusion === 'successful') {
          this.projectCreateKeyIsCompleted = true;
          this.store.dispatch(TriggerProjectOrchestration());
        }
      }
    });
  }

  initWatcher() {
    if (this.browserStorageManagementService.getLocalStorageItem('tour') === 'done') {
      return;
    }

    this.startWatching();
  }

  startWatching() {
    interval(1000).pipe(
      map(() => this.browserStorageManagementService.getLocalStorageItem('tour')),
      distinctUntilChanged(),
      takeWhile(val => val !== 'nineth', true)
    ).subscribe(val => {
      if (val === 'third') {
        this.onValueFour();
      }
    });
  }

  onValueFour() {
    this.showSteps();
  }

  subscribeToClientLocalState() {
    return this.store.select(selectClientLocalStates).subscribe(clientLocalState => {
      this.clientLocalState = cloneDeep(clientLocalState);
    });
  }

  /**
   * Subscribe active person
   *
   * @returns
   * @memberof AppComponent
   */
  subscribeToActivePerson() {
    return this.store.select(state => state.auth?.activePerson)
      .subscribe(activePerson => {
        if (activePerson) {
          this.isPersonVerified = activePerson?.isVerified && activePerson?.interest;
          this.activePerson = activePerson;
          this.verifyPerson();
          // set active person to root DOM for Zendesk
          window['activePerson'] = activePerson;
          this.setThemeBySystemTheme();
        } else {
          this.activePerson = null;
        }
      });
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(activeProject => {
      if (!activeProject) {
        this.activeProject = null;
      } else {
        const currentActiveProjectId = this.activeProject?.id;
        if (activeProject?.id && currentActiveProjectId !== activeProject?.id) {
          this.activeProject = cloneDeep(activeProject);
          this.projectCreateKeyIsCompleted = false;
        }
      }
      this.updateWorkspaceMenuHomePageItemPath();
      this.subscribeToDataAfterActiveProject();
    });
  }

  subscribeToProjectDesignTasks() {
    return this.store.select(selectProjectDesignTasks).subscribe(projectDesignTasks => {
      if (!projectDesignTasks) {
        this.projectDesignTasksLoaded = false;
        this.wizardStepsConfigs = [];
        this.activeWizardStep = undefined;
      } else {
        this.setProjectDesignTasks(projectDesignTasks);
      }
    });
  }

  subscribeToPermissions() {
    return this.permissionService.hasPermission('invite-user')
      .subscribe(hasInvitePermission => {
        this.canInviteUsers = hasInvitePermission;
      });
  }

  setProjectDesignTasks(projectDesignTasks: ProjectDesignTask[]) {
    console.log('* Project Design Tasks:', projectDesignTasks);
    this.projectDesignTasks = projectDesignTasks;
    if (this.projectDesignTasks && this.activeProject?.id) {
      // calculate the wizard step configs
      const { activeStepIndex, wizardStepConfig } =
        this.projectDesignService.calculateWizardStepConfigs(projectDesignTasks, this.activeProject);
      this.wizardStepsConfigs = [...wizardStepConfig];
      console.log('* Wizard Steps Configs:', this.wizardStepsConfigs);
      // calculate the active step
      this.activeWizardStep = activeStepIndex;
      this.projectDesignTasksLoaded = true;
      // set design and code path
      const designPath = this.wizardStepsConfigs[this.activeWizardStep]?.layoutPaths?.designPath;
      const stepLayoutMode: LayoutMode = this.wizardStepsConfigs[this.activeWizardStep]?.layoutMode as LayoutMode;

      if (stepLayoutMode) {
        this.setLayoutMode(stepLayoutMode);
      }

      // Decide if we should display the wizard;
      // filter the wizard steps by displayInPlanningTimeline field to find planning steps
      // check if the last planning step is completed
      const planningWizardSteps = this.activeProject?.isCreatedByAI ? this.wizardStepsConfigs.filter(step => step.displayInPlanningTimeline) : [];
      const completedWizardSteps = planningWizardSteps.filter(wizardStep =>
        wizardStep.status === WizardStepStatus.Completed);
      const isPlanningStepsCompleted = completedWizardSteps?.length >= planningWizardSteps?.length;
      this.displayWizard = !isPlanningStepsCompleted;

      // check if the active step index is in range and the completed steps are less than the total steps
      if (!isPlanningStepsCompleted) {

        if (stepLayoutMode) {
          this.setLayoutMode(stepLayoutMode);
        }

        if (designPath) {
          this.navigateToUrl(designPath);
        }
      } else {
        this.setLayoutMode(this.layoutMode);
      }

    } else {
      this.projectDesignTasksLoaded = false;
      this.displayWizard = false;
    }
  }

  subscribeToUser() {
    return this.store.select(state => state.auth)
      .subscribe(auth => {
        this.authenticatedUser = auth.user;
        const activePerson = auth.activePerson;
        this.verifyPerson();
        /* reset person verification alert config */
        this.PERSON_VERIFICATION_ALERT_CONFIG = null;
        if (this.authenticatedUser && activePerson) {
          if (this.authenticatedUser.usernameType === UsernameType.Email) {
            /* set person verification alert config */
            this.PERSON_VERIFICATION_ALERT_CONFIG = activePerson?.interest ? PERSON_EMAIL_VERIFY_ALERT_CONFIG : null;
          } else if (this.authenticatedUser.usernameType === UsernameType.PhoneNumber) {
            /* set person verification alert config */
            this.PERSON_VERIFICATION_ALERT_CONFIG = activePerson?.interest ? PERSON_PHONE_NUMBER_VERIFY_ALERT_CONFIG : null;
          }
        }
        if (auth.isPermissionLoaded && auth.tenant) {
          if (auth.permissions.includes(Permission.Admin)) {
            this.store.dispatch(SubscriptionAlertVisibility({ payload: { subscriptionAlertVisibility: false, subscriptionAlertVisibilityPermanently: false } }));
          } else if (auth?.tenant?.role?.key === TierType.FreeTier) {
            this.store.dispatch(SubscriptionAlertVisibility({ payload: { subscriptionAlertVisibility: true, subscriptionAlertVisibilityPermanently: true } }));
          } else if (auth?.tenant?.role?.key === TierType.BuilderTier) {
            this.store.dispatch(SubscriptionAlertVisibility({ payload: { subscriptionAlertVisibility: false, subscriptionAlertVisibilityPermanently: false } }));
          }
          this.updateWorkspaceFooterMenu(auth.permissions.includes(Permission.Admin));
        }
      });
  }

  subscribeToAppSearchVisibility() {
    return this.store.select(state => state.app.appSearch.visibility)
      .subscribe(visibility => this.appSearchVisibility = visibility);
  }

  subscribeToLeftSideBarCollapseStatus() {
    return this.store.select(state => state.app.leftSidebarCollapsed)
      .subscribe(leftSidebarCollapsed => {
        console.log('*** AppComponent subscribeToLeftSideBarCollapseStatus', leftSidebarCollapsed);
        this.isWorkSpaceMenuCollapsed = !!leftSidebarCollapsed;
        this.updateWorkspaceMenuHomePageItemPath();
      });
  }

  subscribeToLeftSideBarMenuPinned() {
    return this.store.select(state => state.app.isLeftSideBarPinned)
      .subscribe(isLeftSideBarPinned => {
        this.isWorkSpaceMenuPinned = !!isLeftSideBarPinned;
      });
  }

  verifyPerson() {
    console.log('*** AppComponent verifyPerson');
    if (this.activePerson?.isVerified && this.activePerson?.interest) {
      this.activePersonOnboardingCompleted = true;
    }
  }

  updateWorkspaceFooterMenu(isAdmin: boolean) {
    console.log('*** AppComponent updateWorkspaceFooterMenu');
    const uiPageTemplatesIndex = this.workSpaceFooterMenu.items[0].children.findIndex(
      item => item.key === 'ui-page-templates'
    );
    const languageMenuIndex = this.workSpaceFooterMenu.items.findIndex(
      item => item.key === 'language'
    );

    if (isAdmin && uiPageTemplatesIndex === -1) {
      this.workSpaceFooterMenu.items[0].children.push({
        label: 'UI Page Templates',
        tooltip: 'UI Page Templates',
        key: 'ui-page-templates',
        showLabel: true,
        icon: {
          name: 'fa-thin fa-files'
        },
        actionBehavior: MenuActionBehavior.Emit,
        redirectUrl: PATH_DEFINITIONS.PAGE_TEMPLATES.PAGE_TEMPLATES_LIST_PATH
      });
    } else if (!isAdmin && uiPageTemplatesIndex > -1) {
      this.workSpaceFooterMenu.items[0].children.splice(uiPageTemplatesIndex, 1);
    }

    if (languageMenuIndex > -1 && this.workSpaceFooterMenu.items[languageMenuIndex].children.length < 2) {
      this.workSpaceFooterMenu.items.splice(languageMenuIndex, 1);
    }

    this.workSpaceFooterMenu = { ...this.workSpaceFooterMenu };
  }

  workSpaceMenuItemClicked(clickedMenuItem: Menu) {
    console.log('*** AppComponent workSpaceMenuItemClicked', clickedMenuItem);
    switch (clickedMenuItem.key) {
      case 'english':
        this.languageService.changeLanguage('en');
        break;
      case 'logout':
        this.logout();
        break;
      case 'size-large':
        this.setScale(1.4);
        break;
      case 'size-medium':
        this.setScale(1.2);
        break;
      case 'size-small':
        this.setScale(1);
        break;
      case 'design-view':
        this.setLayoutMode(LayoutMode.ProjectDesign);
        break;
      case 'code-view':
        this.setLayoutMode(LayoutMode.SourceCode);
        break;
      case 'tpm-view':
        this.setLayoutMode(LayoutMode.TPM);
        break;
      case 'ui-elements':
        this.navigateTo(clickedMenuItem.redirectUrl);
        break;
      default:
        if (clickedMenuItem.actionBehavior === MenuActionBehavior.Router) {
          const redirectUrl = clickedMenuItem.redirectUrl.replace(':projectId', this.activeProject?.id || '');
          this.navigateTo(redirectUrl, clickedMenuItem.queryParams);
        }
        break;
    }
  }

  onMegaMenuVisibilityChanged(isMegaMenuVisible: boolean) {
    console.log('*** AppComponent onMegaMenuVisibilityChanged', isMegaMenuVisible);
    this.megaMenuVisible = isMegaMenuVisible;
    if (!isMegaMenuVisible && !this.workspaceMenuIsHovering) {
      // auto-collapse the left sidebar when mega menu is closed and the workspace menu is not hovering
      this.onHoverLeaveWorkspaceMenu();
    }
  }

  onHasOpenOverlaidSubmenuStatusChanged(hasOpenOverlaidSubmenu: boolean) {
    console.log('*** AppComponent onHasOpenOverlaidSubmenuStatusChanged', hasOpenOverlaidSubmenu);
    if (hasOpenOverlaidSubmenu) {
      // there is an open overlaid submenu, DO NOT auto-collapse the left sidebar
      this.hasOpenOverlaidSubmenu = true;
    } else {
      // there is no open overlaid submenu, auto-collapse the left sidebar
      this.hasOpenOverlaidSubmenu = false;
      if (!this.workspaceMenuIsHovering) {
        this.onHoverLeaveWorkspaceMenu();
      }
    }
  }

  channelMenuItemClicked(clickedMenuItem: Menu) {
    console.log('*** AppComponent channelMenuItemClicked', clickedMenuItem);
    if (clickedMenuItem.actionBehavior === MenuActionBehavior.Router) {
      const redirectUrl = clickedMenuItem.redirectUrl.replace(':projectId', this.activeProject?.id || '');
      this.navigateTo(redirectUrl, clickedMenuItem.queryParams);
    }
  }

  setLayoutMode(layoutMode: LayoutMode | undefined | null) {
    if (this.layoutMode !== layoutMode) {
      console.log('*** AppComponent setLayoutMode: New layout', layoutMode, 'Old layout', this.layoutMode);
      this.layoutMode = layoutMode;
      this.setSplitterLayout(layoutMode);
      // this.setVisibilityOfWizardIntroduction();
    }
  }

  navigateTo(path: string, queryParams?: Record<string, unknown>) {
    // if the path is not the same as the current path, then navigate
    if (this.activePath !== path) {
      this.router.navigate([path], { queryParams: queryParams });
    }
  }

  navigateToUrl(url: string) {
    if (this.activePath !== url) {
      this.store.dispatch(new Navigate({ url }));
    }
  }

  /* TODO: Move to menu service. This shouldnt be in this component  */
  setMenuTag(menuItem: Menu, content: string, tagColor?: string) {
    console.log('*** AppComponent setMenuTag');
    menuItem.tag = content;
    menuItem.tagColor = tagColor;
  }

  resendVerifyEmailOrSms() {
    if (this.authenticatedUser?.usernameType === UsernameType.Email) {
      this.store.dispatch(new SendVerifyEmail());
      this.store.dispatch(new Navigate({ url: PATH_DEFINITIONS.AUTH.ACCOUNT_VERIFICATION_PATH }));
    } else if (this.authenticatedUser.usernameType === UsernameType.PhoneNumber) {
      this.store.dispatch(new SendVerifySms());
      this.store.dispatch(new Navigate({ url: PATH_DEFINITIONS.AUTH.VERIFY_PHONE_NUMBER_PATH }));
    }
  }

  onRouteChange() {
    console.log('*** AppComponent onRouteChange');
    /* whenever route changes, page will scroll to top */
    this.scrollService.scrollToTop();
  }


  logout() {
    this.store.dispatch(new Logout());
  }

  onRightSidebarToggle(tabName: AppRightSidebarTab) {
    this.store.dispatch(new ToggleRightSidebarVisibility());
  }

  setScale(scaleValue) {
    const defaultBodyFontSize = 14;
    const fontScale = scaleValue * defaultBodyFontSize;
    document.documentElement.style.fontSize = `${fontScale}px`;
    // document.getElementById('main-layout').style.transform = `scale(${scaleValue})`;
  }

  pricingCloseButtonClick() {
    this.isHomePricingVisible = false;
    this.isHomePricingVisiblePermanently = false;
    this.store.dispatch(SubscriptionAlertVisibility({ payload: { subscriptionAlertVisibility: false, subscriptionAlertVisibilityPermanently: true } }));
  }

  navigateToPayment() {
    this.router.navigateByUrl('/payment');
  }

  splitDragEnds(event) {
    console.log('splitDragEnds', event);
    if (event?.sizes?.length) {
      this.splitterLeftContainerSize = event.sizes[0];
      this.splitterRightContainerSize = event.sizes[1];
      this.saveSplitterSizesToLocalStorage(); // save the sizes to local storage to load them on next initialization
    }
  }

  verticalSplitDragEnds(event) {
    console.log('verticalSplitDragEnds', event);
    if (event?.sizes?.length) {
      this.splitterSourceCodeContainerSize = event.sizes[0];
      this.splitterRouterOutletContainerSize = event.sizes[1];
    }
  }

  onBlueprintManagerCollapseButtonClicked() {
    this.splitterLeftContainerSize = 0;
    this.splitterRightContainerSize = 100;
    this.saveSplitterSizesToLocalStorage(); // save the sizes to local storage to load them on next initialization
  }

  onResetLayout() {
    this.splitterLeftContainerSize = this.splitterLeftContainerSizeDefault;
    this.splitterRightContainerSize = this.splitterRightContainerSizeDefault;
    this.splitterSourceCodeContainerSize = this.splitterSourceCodeContainerSizeDefault;
    this.splitterRouterOutletContainerSize = this.splitterRouterOutletContainerSizeDefault;
    this.saveSplitterSizesToLocalStorage(); // save the sizes to local storage to load them on next initialization
  }

  onDisplayAllPanelsClicked() {
    this.setSplitterLayout(LayoutMode.TPM);
  }

  onDisplayBlueprintPanelClicked() {
    if (!this.layoutMode) {
      this.setLayoutMode(LayoutMode.ProjectDesign);
    }
    if (this.splitterLeftContainerSize < this.splitterLeftContainerSizeDefault) {
      this.splitterLeftContainerSize = this.splitterLeftContainerSizeDefault;
      this.splitterRightContainerSize = this.splitterRightContainerSizeDefault;
      this.saveSplitterSizesToLocalStorage(); // save the sizes to local storage to load them on next initialization
    }
  }

  onToggleBlueprintManager() {
    if (this.splitterLeftContainerSize) {
      this.onBlueprintManagerCollapseButtonClicked();
    } else {
      this.onDisplayBlueprintPanelClicked();
    }
  }

  onDisplayDesignPanelClicked() {
    this.setLayoutMode(LayoutMode.ProjectDesign);
    this.splitterSourceCodeContainerSize = 0;
    this.splitterRouterOutletContainerSize = 100;
    if (this.splitterRightContainerSize < this.splitterRightContainerSizeDefault) {
      this.splitterLeftContainerSize = this.splitterLeftContainerSizeDefault;
      this.splitterRightContainerSize = this.splitterRightContainerSizeDefault;
      this.saveSplitterSizesToLocalStorage(); // save the sizes to local storage to load them on next initialization
    }
  }

  onDisplayCodePanelClicked() {
    this.setLayoutMode(LayoutMode.SourceCode);
    this.splitterSourceCodeContainerSize = 100;
    this.splitterRouterOutletContainerSize = 0;
    if (this.splitterRightContainerSize < this.splitterRightContainerSizeDefault) {
      this.splitterLeftContainerSize = this.splitterLeftContainerSizeDefault;
      this.splitterRightContainerSize = this.splitterRightContainerSizeDefault;
      this.saveSplitterSizesToLocalStorage(); // save the sizes to local storage to load them on next initialization
    }
  }

  onWizardModalCancelled() {
    console.log('*** AppComponent onWizardModalCancelled');
    this.displayWizardModal = false;
  }

  onWizardModalNextButtonClicked() {
    console.log('*** AppComponent onWizardModalNextButtonClicked');
    // triggerNextWizardStep()
  }

  onWizardModalTypingsCompleted(event) {
    console.log('*** AppComponent onWizardModalTypingsCompleted', event);

    if (event.typingsCompleted) {
      if (event.wizardStep === this.activeWizardStep && event.projectId === this.activeProject.id) {
        this.triggerNextWizardStep();
      }

    }
  }

  triggerNextWizardStep() {
    this.store.dispatch(NextStep({ payload: { activeProjectId: this.activeProject.id } }));
  }

  onMegaMenuClicked(event: any) {
    if (event?.path) {
      this.setLayoutMode(LayoutMode.ProjectDesign);
    }
    this.megaMenuVisible = false;
    this.onHoverLeaveWorkspaceMenu();
  }

  onMegaMenuClose() {
    this.megaMenuVisible = false;
    this.onHoverLeaveWorkspaceMenu();
  }

  showSteps() {
    this.joyrideService.startTour(
      { steps: ['firstOptional', 'secondOptional', 'third', 'thirdOptional', 'fourthOptional', 'fifthOptional', 'sixthOptional', 'seventhOptional'], themeColor: '#2d2f34', waitingTime: 500, customTexts: { next: 'Next', prev: 'Prev', done: 'Done' }, showPrevButton: false, startWith: 'third' }
    ).subscribe(
      (step) => {
      },
      (error) => {
      },
      () => {
      }
    );
  }

  onNext(step) {
    if (step === 'third') {
      this.browserStorageManagementService.setLocalStorageItem('tour', 'fourth');
      this.router.navigate(['/ai/datasources'], { queryParams: { step: 'fourth' } });
    }
  }

  autoCollapseWorkspaceMenu() {
    if (!this.isWorkSpaceMenuCollapsed && !this.isWorkSpaceMenuPinned && !this.hasOpenOverlaidSubmenu) {
      this.store.dispatch(new CollapseLeftSidebar());
    }
  }

  triggerToCollapseWorkspaceMenu() {
    setTimeout(() => {
      this.autoCollapseWorkspaceMenu();
    }, 5000);
  }

  onHoverEnterWorkspaceMenu() {
    this.workspaceMenuIsHovering = true;
    this.store.dispatch(new ExpandLeftSidebar({ isMenuPinned: this.isWorkSpaceMenuPinned }));
  }

  onHoverLeaveWorkspaceMenu() {
    this.workspaceMenuIsHovering = false;
    if (!this.isWorkSpaceMenuPinned && !this.hasOpenOverlaidSubmenu && !this.megaMenuVisible) {
      this.store.dispatch(new CollapseLeftSidebar());
    }
  }
}

// Disable console logs in no-local environmentÍ
if (environment.production) {
  (function () {
    const originalLog = console.log;
    console.log = function (...args) {
      // do nothing
    };
  })();
}
